import React, { useEffect, useState, useRef } from 'react';
import Navbar from '../components/Navbar';
import { AlertCircle, Calendar, Clock, MapPin, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { formatDateString } from '../lib/utils';
import { Badge } from '@/components/ui/badge';
import { useSearchParams } from 'react-router-dom';

// Import our custom hooks
import {
  useCheckInAnimation,
  scrollToElementWithAnimation,
  useCheckInUtils,
  useParentPlayers,
  useAnonPlayerSearch,
  useSessions,
  useCheckInProcess
} from '../hooks/checkin';

// Define type for age group nested within session
interface SessionAgeGroup {
  id: string;
  group_name: string;
  sex: 'male' | 'female';
}

// Define type for the linking table entry
interface EvaluationSessionAgeGroup {
  age_group_id: string;
  age_groups: SessionAgeGroup | null; // Allow null in case join fails
}

// Define type for EvaluationSession (simplified for check-in context)
interface EvaluationSession {
  id: string;
  session_date: string;
  start_time: string;
  end_time: string;
  location_name: string;
  evaluation_session_age_groups: EvaluationSessionAgeGroup[];
}

export default function CheckIn() {
  // State for managing the check-in step
  const [step, setStep] = useState(1); // 1: Find/Select Player, 2: Select Session, 3: Enter Jersey
  // const [justCheckedIn, setJustCheckedIn] = useState(false); // Flag for successful check-in - REMOVED

  // Use our custom hooks
  useCheckInAnimation();
  const { isParentMode, forceDate, convertToAmPm } = useCheckInUtils();

  const {
    parentPlayers,
    selectedParentPlayerId,
    selectedPlayer: parentSelectedPlayer,
    handleSelectParentPlayer,
    resetParentPlayerSelection
  } = useParentPlayers(isParentMode);

  const [searchParams] = useSearchParams();
  const lastNameParam = searchParams.get('lastname');
  const birthDateParam = searchParams.get('birthdate');

  const {
    anonLastName,
    setAnonLastName,
    anonBirthdate,
    setAnonBirthdate,
    foundPlayers, // Keep this in case UI needs to show multiple results later
    selectedPlayer: anonSelectedPlayer,
    setSelectedPlayer: setAnonSelectedPlayer, // Direct selection needed for reset and error handling
    isSearching,
    error: searchError, // Destructure and rename error to searchError
    handleAnonSearch,
    resetSearch
  } = useAnonPlayerSearch();

  // Determine which selectedPlayer to use based on the mode
  const selectedPlayer = isParentMode ? parentSelectedPlayer : anonSelectedPlayer;

  const {
    todaysSessions,
    selectedSession,
    setSelectedSession,
    error: sessionsError
  } = useSessions(selectedPlayer, forceDate);
  
  // Reference to the Next button in Step 2
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const {
    jerseyNumber,
    setJerseyNumber,
    jerseyColor,
    setJerseyColor,
    isCheckingIn,
    message,
    setMessage, // Needed for useEffect clear
    checkInError,
    setCheckInError, // Needed for useEffect clear
    confirmDialogOpen,
    setConfirmDialogOpen,
    pendingCheckIn,
    handleCheckIn,
    confirmCheckIn,
    cancelCheckIn
  } = useCheckInProcess();

  // --- NEW STATE for handling multiple player selection --- 
  const [showPlayerSelection, setShowPlayerSelection] = useState(false);

  // --- NEW STATE for gender filter --- 
  const [genderFilter, setGenderFilter] = useState<'Boys' | 'Girls' | 'All'>('All');

  // Effect to move to step 2 when a player is selected/found in step 1
  useEffect(() => {
    if (selectedPlayer && step === 1) {
      // Automatically move to step 2
      setStep(2);
      // Clear any potential leftover session/jersey/messages from a previous incomplete flow
      setSelectedSession(null);
      setJerseyNumber('');
      setMessage('');
      setCheckInError('');
      // --- NEW --- Hide player selection if user somehow got here after selecting
      setShowPlayerSelection(false); 
    }
  }, [selectedPlayer, step, setSelectedSession, setJerseyNumber, setMessage, setCheckInError]);

  // --- NEW EFFECT --- Show player selection list when multiple players are found
  useEffect(() => {
    if (!isSearching && foundPlayers.length > 1 && !selectedPlayer) {
      setShowPlayerSelection(true);
    } else {
      // Hide selection if searching again, or if only 0/1 players found, or if a player is selected
      setShowPlayerSelection(false);
    }
  }, [isSearching, foundPlayers, selectedPlayer]);

  // Handle potential session errors loading
  useEffect(() => {
    if (sessionsError) {
      setCheckInError(`Error loading sessions: ${sessionsError}`);
    }
  }, [sessionsError, setCheckInError]);

  // Enhanced form submission for anonymous search to handle API errors
  const submitAnonSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setMessage(''); // Clear any previous success/error message from useCheckInProcess
    // handleAnonSearch will set its own searchError if needed
    const playersFound = await handleAnonSearch(); // Call and await the result
    if (playersFound && playersFound.length > 1) { // Check if playersFound is not null and has more than one player
      setShowPlayerSelection(true);
    } else {
      setShowPlayerSelection(false);
      // If only one player is found, handleAnonSearch already sets selectedPlayer
      // If no players are found, handleAnonSearch sets its error state
    }
  };

  // Handle local check-in initiation (called from Step 3)
  const initiateCheckIn = () => {
    if (selectedPlayer && selectedSession && jerseyNumber && jerseyColor) {
        setMessage(''); // Clear previous messages
        setCheckInError(''); // Clear previous errors
        handleCheckIn(selectedPlayer, selectedSession, jerseyNumber, jerseyColor);
    }
  };

  // Handle stepping forward (from session selection Step 2 to jersey entry Step 3)
  const handleNextFromSession = () => {
    if (selectedSession) {
      setMessage(''); // Clear messages/errors when navigating
      setCheckInError('');
      setStep(3);
    }
  };

  // Handle stepping back between steps
  const handleBack = () => {
    setMessage(''); // Clear messages/errors when navigating back
    setCheckInError('');
    if (step === 3) { // Back from Jersey Entry (Step 3) to Session Select (Step 2)
      setJerseyNumber(''); // Clear jersey number
      setJerseyColor(null);
      setStep(2);
    } else if (step === 2) { // Back from Session Select (Step 2) to Player Find/Select (Step 1)
      setSelectedSession(null); // Clear selected session
      // Reset player selection based on mode
      if (isParentMode) {
        resetParentPlayerSelection(); // This should set parentSelectedPlayer to null
      } else {
        resetSearch(); // This should clear anonLastName, anonBirthdate, foundPlayers, and anonSelectedPlayer
      }
      // Explicitly set step back to 1. The player reset should trigger step 1 view.
      setStep(1);
    }
  };


  // Reset everything on successful check-in confirmation
  const onSuccessfulCheckIn = () => {
    // Reset player selections
    resetSearch(); // Clears anonSelectedPlayer, anonLastName, anonBirthdate, foundPlayers, searchError
    if (isParentMode) {
      resetParentPlayerSelection(); // Clears selectedParentPlayerId, parentSelectedPlayer
    }
    setSelectedSession(null); // Clear selected session
    // setJerseyNumber(''); // This is already reset in useCheckInProcess.confirmCheckIn's finally block
    
    // Important: Go back to step 1
    setStep(1);
    // The success message is set by useCheckInProcess and will be displayed in Step 1.
    // It will be cleared when the user starts a new action in Step 1.
  };

  // Effect to clear step-specific errors when step changes
  useEffect(() => {
    // We no longer clear the main 'message' here.
    // It will be cleared by user actions in Step 1 (e.g., new search, new selection).
    setCheckInError(null); // Always clear check-in specific errors (from Step 3)
  }, [step, setCheckInError]); // Removed setMessage and justCheckedIn from dependencies

  // Update anonymous search fields from query params if present
  useEffect(() => {
    if (lastNameParam) {
      setAnonLastName(lastNameParam);
    }
    if (birthDateParam) {
      setAnonBirthdate(birthDateParam);
    }
  }, [lastNameParam, birthDateParam]);

  return (
    <div>
      <Navbar />
      <div className="flex justify-center my-6 animate-fade-in">
        <div className="relative">
          <img
            src="/cdo.png"
            alt="CDO Soccer Logo"
            className="h-36 md:h-44 object-contain drop-shadow-lg transition-transform hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white opacity-20 rounded-full"></div>
        </div>
      </div>
      <main className="container mx-auto p-4 flex flex-col items-center">
        <h1 className="text-2xl font-bold mb-6">
          {isParentMode ? 'Player Check-In (Parent)' : 'Player Check-In'}
        </h1>

        {/* --- MESSAGES/ERRORS --- Always visible */}
        {/* Check-in Process Messages/Errors */}
        {checkInError && !isSearching && ( // Avoid showing stale check-in errors during search
          <Alert variant="destructive" className="mb-4 w-full max-w-md">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{checkInError}</AlertDescription>
          </Alert>
        )}
         {message && ( // Success messages from check-in
           <Alert variant="success" className="mb-4 w-full max-w-md">
             <AlertCircle className="h-4 w-4" />
             <AlertTitle>Success</AlertTitle>
             <AlertDescription>{message}</AlertDescription>
           </Alert>
         )}


        {/* --- STEP 1: FIND/SELECT PLAYER --- */}
        {/* Show this section only when we are on step 1 AND no player is selected yet */}
        {step === 1 && !selectedPlayer && (
          <div className="w-full max-w-md animate-slide-in"> {/* Wrapper for animation */}
            {/* Parent Mode */}
            {isParentMode && (
              <Card className="w-full mb-6">
                <CardHeader>
                  <CardTitle>Step 1 of 3: Select Your Player</CardTitle>
                  <CardDescription>Select which of your players you want to check in.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {parentPlayers.length === 0 && !checkInError && ( // Don't show loading if there was an error
                    <p className="text-sm text-gray-500">Loading your players...</p>
                  )}
                  {parentPlayers.length > 0 && (
                    <div>
                      <Label htmlFor="parentPlayerSelect">Select Player</Label>
                      <Select
                        value={selectedParentPlayerId}
                        onValueChange={(value) => {
                          setMessage(''); // Clear messages/errors on new selection
                          setCheckInError('');
                          handleSelectParentPlayer(value);
                          // Step transition handled by useEffect watching selectedPlayer
                        }}
                      >
                        <SelectTrigger id="parentPlayerSelect">
                          <SelectValue placeholder="-- Choose your player --" />
                        </SelectTrigger>
                        <SelectContent>
                          {parentPlayers.map((player) => (
                            <SelectItem key={player.id} value={player.id}>
                              {player.first_name} {player.last_name} ({player.birthdate})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Anonymous Mode */}
            {!isParentMode && (
              <div className="space-y-6">
                <p className="text-sm text-gray-600">
                  If you haven't registered, please use the QR code or see staff.
                </p>
                {/* Display Search Error from useAnonPlayerSearch */} 
                {searchError && (
                  <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Search Error</AlertTitle>
                    <AlertDescription>{searchError}</AlertDescription>
                  </Alert>
                )}
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Player Search</CardTitle>
                    <CardDescription>Enter player's last name and birthdate to find them.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Anonymous Player Search Form - Hide if showing selection */}
                    {!showPlayerSelection && (
                      <form onSubmit={submitAnonSearch} className="space-y-4">
                        <div>
                          <Label htmlFor="anonLastName">Last Name</Label>
                          <Input
                            id="anonLastName"
                            autoComplete="off"
                            type="text"
                            placeholder="Smith"
                            className="w-full"
                            value={anonLastName}
                            onChange={(e) => { setAnonLastName(e.target.value); setMessage(''); setCheckInError(''); }} // Clear messages
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="anonBirthdate">Player's Birthdate</Label>
                          <Input
                            id="anonBirthdate"
                            type="date"
                            placeholder="YYYY-MM-DD"
                            className="w-full min-w-[220px] sm:min-w-[260px] md:min-w-[300px] max-w-xs text-center"
                            value={anonBirthdate}
                            onChange={(e) => { setAnonBirthdate(e.target.value); setMessage(''); setCheckInError(''); }} // Clear messages
                            required
                          />
                        </div>
                        <Button type="submit" disabled={isSearching} className="w-full">
                          {isSearching ? 'Searching...' : 'Find Player'}
                        </Button>
                      </form>
                    )}

                    {/* --- NEW Player Selection Section --- */} 
                    {showPlayerSelection && (
                      <div className="mt-4 space-y-3">
                        <h3 className="text-lg font-semibold">Multiple Players Found</h3>
                        <p className="text-sm text-muted-foreground">Please select the correct player:</p>
                        <div className="space-y-2">
                          {foundPlayers.map((player) => (
                            <Button
                              key={player.id}
                              variant="outline"
                              className="w-full justify-start text-left h-auto py-2 px-3"
                              onClick={() => {
                                setAnonSelectedPlayer(player);
                                setMessage(''); // Clear previous success message
                                // No need to setShowPlayerSelection(false) here, useEffect handles it
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{player.first_name} {player.last_name}</span>
                                <span className="text-xs text-muted-foreground">Born: {formatDateString(player.birthdate)}</span>
                              </div>
                            </Button>
                          ))}
                        </div>
                         <Button 
                            variant="secondary" 
                            className="w-full mt-2"
                            onClick={() => {
                                resetSearch(); // Clears found players, error, inputs
                                setShowPlayerSelection(false); // Explicitly hide selection
                                setMessage(''); // Clear previous success message
                            }}
                          > 
                           Search Again
                         </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}


        {/* --- STEP 2: Select Session --- */}
        {/* Show only on step 2 AND when a player is selected */}
        {selectedPlayer && step === 2 && (
          <Card className="w-full max-w-md mb-6 animate-slide-in">
            <CardHeader>
              <CardTitle>Step 2 of 3: Select Session</CardTitle>
              <CardDescription>
                Select the evaluation session for <span className="font-semibold">{selectedPlayer.first_name} {selectedPlayer.last_name}</span>.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* --- NEW GENDER FILTER --- */}
              <div className="mb-6">
                <h2 className="text-xl font-semibold mb-2">Filter by Gender</h2>
                <div className="flex gap-4">
                  <button
                    className={`px-4 py-2 rounded-md ${genderFilter === 'All' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    onClick={() => setGenderFilter('All')}
                  >
                    All
                  </button>
                  <button
                    className={`px-4 py-2 rounded-md ${genderFilter === 'Boys' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    onClick={() => setGenderFilter('Boys')}
                  >
                    Boys Only
                  </button>
                  <button
                    className={`px-4 py-2 rounded-md ${genderFilter === 'Girls' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                    onClick={() => setGenderFilter('Girls')}
                  >
                    Girls Only
                  </button>
                </div>
              </div>

              {/* Session List */}
              <div className="space-y-3">
                {todaysSessions.filter(session => 
                  session.evaluation_session_age_groups.some((ageGroup: EvaluationSessionAgeGroup) => 
                    ageGroup.age_groups && 
                    (genderFilter === 'All' || 
                     (genderFilter === 'Boys' && ageGroup.age_groups.sex === 'male') || 
                     (genderFilter === 'Girls' && ageGroup.age_groups.sex === 'female'))
                  )
                ).map((session: EvaluationSession) => (
                  <div
                    key={session.id}
                    className={`border rounded-lg overflow-hidden cursor-pointer transition-all duration-150 ease-in-out group ${selectedSession?.id === session.id ? 'ring-2 ring-indigo-500 border-indigo-500 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'}`}
                    onClick={() => {
                      setSelectedSession(session); 
                      setCheckInError('');
                      setMessage('');
                      // After selecting, scroll to the Next button with animation
                      // Use a slight delay to ensure state updates first
                      setTimeout(() => scrollToElementWithAnimation(nextButtonRef.current, true), 100);
                    }} // Clear session loading error on select
                  >
                    {/* Header with Time */}
                    <div className={`px-4 py-3 border-b flex items-center justify-between ${selectedSession?.id === session.id ? 'bg-indigo-50 border-indigo-200' : 'bg-gray-50 border-gray-200 group-hover:bg-gray-100'}`}>
                      <div className="flex items-center">
                        <Clock className={`w-5 h-5 mr-2 ${selectedSession?.id === session.id ? 'text-indigo-600' : 'text-gray-600'}`} />
                        <h3 className={`font-semibold ${selectedSession?.id === session.id ? 'text-indigo-900' : 'text-gray-900'}`}>
                          {convertToAmPm(session.start_time)}
                        </h3>
                      </div>
                      <ChevronRight className={`w-5 h-5 transition-colors ${selectedSession?.id === session.id ? 'text-indigo-500' : 'text-gray-400 group-hover:text-indigo-500'}`} />
                    </div>

                    {/* Body with Details */}
                    <div className="p-4 space-y-3">
                      {/* Age Groups */}
                      <div className="flex flex-wrap gap-1">
                        {session.evaluation_session_age_groups && session.evaluation_session_age_groups.length > 0 ? (
                          session.evaluation_session_age_groups.map((groupInfo: EvaluationSessionAgeGroup) => (
                            groupInfo.age_groups && (
                              <Badge
                                key={groupInfo.age_group_id}
                                variant="outline-sm"
                                className={`py-1 px-2 font-medium border ${groupInfo.age_groups.sex === 'male' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-pink-50 border-pink-200 text-pink-700'}`}
                              >
                                {groupInfo.age_groups.group_name} {groupInfo.age_groups.sex === 'male' ? 'Boys' : 'Girls'}
                              </Badge>
                            )
                          ))
                        ) : (
                          <span className="text-sm text-gray-500 italic">No age groups assigned</span>
                        )}
                      </div>

                      {/* Location and Time Range */}
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center">
                          <MapPin className="w-3 h-3 text-gray-400 mr-1.5" />
                          <span>{session.location_name}</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-3 h-3 text-gray-400 mr-1.5" />
                          <span>
                            {convertToAmPm(session.start_time)} - {convertToAmPm(session.end_time)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Session Loading/Error Messages */}
              {checkInError && checkInError.startsWith("Error loading sessions:") && ( // Show session loading error specifically here
                <p className="text-sm text-red-600">{checkInError}</p>
              )}
              {todaysSessions.length === 0 && !sessionsError && selectedPlayer && ( // Check selectedPlayer to avoid flash before sessions load
                 <p className="text-sm text-red-600">No sessions scheduled for today for this player's age group ({selectedPlayer.birthdate}).</p>
              )}


              {/* Step 2 Navigation Buttons */}
              <div className="flex space-x-2 mt-4">
                 <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack} // Go back to Step 1
                    className="w-full"
                  >
                    Back
                  </Button>
                {/* Only show Next if there are sessions and no session loading error */}
                {todaysSessions.length > 0 && !sessionsError && (
                  <Button
                    ref={nextButtonRef}
                    type="button"
                    onClick={handleNextFromSession} // Go forward to Step 3
                    disabled={!selectedSession} // Disabled if no session is chosen
                    className="w-full bg-blue-600 text-white hover:bg-blue-700 transition"
                  >
                    Next
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}


        {/* --- STEP 3: Enter Jersey Number --- */}
        {/* Show only on step 3 AND when a player AND session are selected */}
        {selectedPlayer && selectedSession && step === 3 && (
          <Card className="w-full max-w-md animate-slide-in">
            <CardHeader>
              <CardTitle>Step 3 of 3: Shirt Number</CardTitle>
              <CardDescription>
                Checking in {selectedPlayer.first_name} {selectedPlayer.last_name} for the session starting at {convertToAmPm(selectedSession.start_time)}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Display Selected Session Info */}
                <div className="p-3 border rounded-md bg-gray-50 text-sm">
                  <p><strong>Session Details:</strong></p>
                  <div className="flex items-center mt-1">
                    <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                    <span>{formatDateString(selectedSession.session_date, undefined, 'medium')}</span>
                  </div>
                  <div className="flex items-center mt-1">
                    <Clock className="w-4 h-4 mr-2 text-gray-500" />
                    <span>{convertToAmPm(selectedSession.start_time)} - {convertToAmPm(selectedSession.end_time)}</span>
                  </div>
                   <div className="flex items-center mt-1">
                     <MapPin className="w-4 h-4 mr-2 text-gray-500" />
                     <span>{selectedSession.location_name}</span>
                   </div>
                </div>
                <div>
                  <Label className="text-lg">Jersey Color</Label>
                  <p className="text-sm text-gray-600 mb-2">Look at the jersey: is the printed number red or blue?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      aria-pressed={jerseyColor === 'red'}
                      onClick={() => { setJerseyColor('red'); setCheckInError(''); setMessage(''); }}
                      className={`rounded-lg py-6 text-xl font-bold transition border-2 ${
                        jerseyColor === 'red'
                          ? 'bg-red-600 text-white border-red-700 ring-2 ring-red-300'
                          : 'bg-white text-red-600 border-red-200 hover:border-red-400'
                      }`}
                    >
                      Red
                    </button>
                    <button
                      type="button"
                      aria-pressed={jerseyColor === 'blue'}
                      onClick={() => { setJerseyColor('blue'); setCheckInError(''); setMessage(''); }}
                      className={`rounded-lg py-6 text-xl font-bold transition border-2 ${
                        jerseyColor === 'blue'
                          ? 'bg-blue-600 text-white border-blue-700 ring-2 ring-blue-300'
                          : 'bg-white text-blue-600 border-blue-200 hover:border-blue-400'
                      }`}
                    >
                      Blue
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-lg" htmlFor="jerseyNumberInput">Assigned Shirt Number</Label>
                  <Input
                    id="jerseyNumberInput"
                    type="text" // Use text to allow easier handling/clearing but enforce numeric input below
                    value={jerseyNumber}
                    onChange={(e) => {
                        const numericValue = e.target.value.replace(/\D/g,''); // Allow only digits
                        setJerseyNumber(numericValue);
                        setCheckInError(''); // Clear error on input
                        setMessage('');
                    }}
                    placeholder="Enter assigned shirt number"
                    className="w-full text-lg"
                    pattern="[0-9]*" // Helps mobile keyboards
                    inputMode="numeric" // Helps mobile keyboards
                    maxLength={3} // Limit to 3 digits usually
                    required
                    aria-required="true"
                  />
                </div>

                {/* Step 3 Navigation Buttons */}
                <div className="flex space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleBack} // Go back to Step 2
                    className="w-full"
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={initiateCheckIn} // Trigger final check-in process
                    disabled={isCheckingIn || !jerseyNumber || jerseyNumber.length === 0 || !jerseyColor}
                    className="w-full bg-indigo-600 text-white hover:bg-indigo-700 transition"
                  >
                    {isCheckingIn ? 'Checking In...' : 'Check In Player'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Confirmation Dialog (remains unchanged functionally) */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Player Check-In</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {pendingCheckIn && (
              <>
                <Alert variant="destructive" className="mb-4 w-full max-w-md">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Please Confirm</AlertTitle>
                  <AlertDescription>Confirm the session and shirt number before checking in the player.</AlertDescription>
                </Alert>
                <ul className="list-disc pl-4 space-y-2">
                  <li>
                    <strong>Player:</strong> {pendingCheckIn.player.first_name} {pendingCheckIn.player.last_name}
                  </li>
                  <li>
                    <strong>Session:</strong> {formatDateString(pendingCheckIn.session.session_date, undefined, 'short')} at {convertToAmPm(pendingCheckIn.session.start_time)}
                  </li>
                  <li>
                    <strong>Age Group{Array.isArray(pendingCheckIn.session.evaluation_session_age_groups) && pendingCheckIn.session.evaluation_session_age_groups.length > 1 ? 's' : ''}:</strong>{' '}
                    {pendingCheckIn.session.evaluation_session_age_groups && pendingCheckIn.session.evaluation_session_age_groups.length > 0
                      ? pendingCheckIn.session.evaluation_session_age_groups
                          .map(
                            (ag: any) =>
                              ag.age_groups
                                ? `${ag.age_groups.group_name}${ag.age_groups.sex ? ` (${ag.age_groups.sex === 'male' ? 'Boys' : 'Girls'})` : ''}`
                                : ''
                          )
                          .filter(Boolean)
                          .join(', ')
                      : 'N/A'}
                  </li>
                  <li>
                    <strong>Shirt Number:</strong> {pendingCheckIn.shirtNumber}
                  </li>
                  <li>
                    <strong>Shirt Color:</strong>{' '}
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-white font-semibold ${
                        pendingCheckIn.shirtColor === 'red' ? 'bg-red-600' : 'bg-blue-600'
                      }`}
                    >
                      {pendingCheckIn.shirtColor.charAt(0).toUpperCase() + pendingCheckIn.shirtColor.slice(1)}
                    </span>
                  </li>
                </ul>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelCheckIn}>
              Cancel
            </Button>
            <Button onClick={async () => {
              // Disable button or show loading state within dialog if needed
              const success = await confirmCheckIn();
              if (success) {
                onSuccessfulCheckIn(); // Reset state after successful confirmation
                // Dialog closes automatically via onOpenChange
              }
              // If !success, error message should be displayed via checkInError state, dialog stays open
            }}>
              {isCheckingIn ? 'Confirming...' : 'Confirm Check-In'} {/* Show loading state here too */}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}